import {
  React,
  useState,
  useEffect,
  Bell,
  Settings,
  User,
  ChevronDown,
  useAppContext,
  useAuth,
  useNavigate,
  SettingsDrawer,
  SettingsSidebar,
  OrganisationProfileForm,
  NumberingConfigForm,
  DocumentCreateForm,
  WhatsAppIntegrationForm,
  WhatsAppCloudTemplatesForm,
  GstSettingsForm,
  EmailSettings,
} from "../utils/commonImports";
import ClientBankForm from "./settings/ClientBankForm";
import DirectTaxSettings from "./settings/DirectTaxSettings";
import EInvoiceSettings from "./settings/EInvoiceSettings";
import MSMESettings from "./settings/MSMESettings";
import Users from "./settings/Users";
import Roles from "./settings/Roles";
import TripsheetSettingsForm from "./settings/TripsheetSettingsForm";
import InvoiceSettingsForm from "./settings/InvoiceSettingsForm";

import axiosInstance from "../api/axiosInstance";

const Header = ({ title, sidebarWidth = 200 }) => {
  console.log("Current header title:", title);

  const {
    company,
    setUser,
    setCompany,
    logout: appLogout,
    financialYears,
    selectedFinancialYear,
    selectFinancialYear,
    financialYearLoading,
  } = useAppContext();

  const { logout: authLogout } = useAuth();

  const navigate = useNavigate();

  const [showMenu, setShowMenu] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [activeCard, setActiveCard] = useState(null);

  const [activeMenu, setActiveMenu] = useState("");

  const [menuKeys, setMenuKeys] = useState([]);
  const [menuPermissionMaster, setMenuPermissionMaster] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  const [selectedDocumentType, setSelectedDocumentType] = useState("");

  // =====================================
  // COMPANY DATA
  // =====================================

  const companyData = company?.client || company || {};

  // =====================================
  // DEBUG
  // =====================================

  useEffect(() => {
    console.log("🏢 Header Company:", companyData);

    console.log("🔴 Account Type:", companyData?.account_type);

    console.log("🔴 Trial Expiry Date:", companyData?.trial_expiry_date);
  }, [company, companyData]);

  useEffect(() => {
    let mounted = true;

    const loadSettingsPermissions = async () => {
      try {
        setPermissionsLoading(true);

        const [masterResponse, permissionsResponse] = await Promise.all([
          axiosInstance.get("/api/roles/menu-permissions/master"),
          axiosInstance.get("/api/roles/my-permissions"),
        ]);

        if (!mounted) {
          return;
        }

        setMenuPermissionMaster(
          Array.isArray(masterResponse.data?.menu_permissions)
            ? masterResponse.data.menu_permissions
            : [],
        );

        setMenuKeys(
          Array.isArray(permissionsResponse.data?.menu_keys)
            ? permissionsResponse.data.menu_keys
            : [],
        );
      } catch (error) {
        console.error("Load header settings permissions error:", error);

        if (mounted) {
          setMenuPermissionMaster([]);
          setMenuKeys([]);
        }
      } finally {
        if (mounted) {
          setPermissionsLoading(false);
        }
      }
    };

    loadSettingsPermissions();

    return () => {
      mounted = false;
    };
  }, []);

  // =====================================
  // TRIAL CHECK
  // =====================================

  const isTrialClient =
    String(companyData?.account_type || "").toUpperCase() === "TRIAL";

  // =====================================
  // FORMAT DATE
  // =====================================

  const formatTrialExpiryDate = (date) => {
    if (!date) {
      return "";
    }

    const d = new Date(date);

    if (Number.isNaN(d.getTime())) {
      return "";
    }

    return d.toLocaleDateString("en-GB");
  };

  // =====================================
  // TRIAL EXPIRY
  // =====================================

  const trialExpiryText = formatTrialExpiryDate(companyData?.trial_expiry_date);

  // =====================================
  // SETTINGS CARDS
  // =====================================

  const settingsCards = [
    {
      permissionKey: "settings_organisation",
      title: "Organisation",
      icon: <span className="text-blue-600 text-2xl">🏢</span>,
      items: ["Profile", "Bank", "Numbering Configuration", "Document Create"],
    },

    {
      permissionKey: "settings_tax_compliance",
      title: "Tax & Compliance",
      icon: <span className="text-teal-600 text-2xl">💰</span>,
      items: ["GST Settings", "Direct Tax", "e-Invoice", "MSME Settings"],
    },

    {
      permissionKey: "settings_users_roles",
      title: "Users & Roles",
      icon: <span className="text-green-600 text-2xl">👥</span>,
      items: ["Users", "Roles"],
    },

    {
      permissionKey: "settings_operation_controls",
      title: "Operation Controls",
      icon: <span className="text-pink-600 text-2xl">⚙️</span>,
      items: [
        // "Estimation",
        // "Booking",
        "Tripsheet",
        "Invoice",
        // "Purchase",
        // "Credit Note",
      ],
    },

    // {
    //   permissionKey: "settings_notifications",
    //   title: "Reminders & Notifications",
    //   icon: <span className="text-yellow-500 text-2xl">🔔</span>,
    //   items: [
    //     "Reminders",
    //     "Email Notifications",
    //     "SMS Notification",
    //     "Whatsapp Notification",
    //   ],
    // },

    {
      permissionKey: "settings_integrations",
      title: "Integrations & Marketplace",
      icon: <span className="text-purple-600 text-2xl">🔗</span>,
      items: [
        "Email Integration",
        "SMS Integration",
        "WhatsApp Cloud API",
        "WhatsApp Templates",
        "Tally Integration",
      ],
    },
  ];

  // =====================================
  // DATABASE-DRIVEN SETTINGS VISIBILITY
  // =====================================

  const visibleSettingsCards = settingsCards.filter(
    (card) => !permissionsLoading && menuKeys.includes(card.permissionKey),
  );

  // =====================================
  // DATABASE-DRIVEN SETTINGS VISIBILITY
  // =====================================

  const normalizePermissionText = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const getMenuKeyForSettingsItem = (item) => {
    const itemValue = normalizePermissionText(item);

    if (!itemValue) {
      return null;
    }

    const masterItem = menuPermissionMaster.find((entry) => {
      const keyValue = normalizePermissionText(entry?.menu_key);
      const labelValue = normalizePermissionText(entry?.label);

      return (
        keyValue === itemValue ||
        labelValue === itemValue ||
        keyValue.endsWith(itemValue) ||
        labelValue.endsWith(itemValue)
      );
    });

    return masterItem?.menu_key || null;
  };

  // const visibleSettingsCards = settingsCards
  //   .map((card) => ({
  //     ...card,
  //     items: card.items.filter((item) => {
  //       const menuKey = getMenuKeyForSettingsItem(item);

  //       return (
  //         !permissionsLoading &&
  //         Boolean(menuKey) &&
  //         menuKeys.includes(menuKey)
  //       );
  //     }),
  //   }))
  //   .filter((card) => card.items.length > 0);

  // =====================================
  // MENU
  // =====================================

  const toggleMenu = () => setShowMenu((prev) => !prev);

  const onViewProfile = () => {
    setShowMenu(false);

    navigate("/profile");
  };

  // =====================================
  // LOGOUT
  // =====================================

  const handleLogout = async () => {
    try {
      await axiosInstance.post("/api/auth/logout");
    } catch (error) {
      console.error("Logout API error:", error);
    } finally {
      authLogout();

      if (appLogout) {
        appLogout();
      } else {
        setUser(null);
        setCompany(null);
      }

      navigate("/login");
    }
  };

  // =====================================
  // OPEN SETTINGS DRAWER
  // =====================================

  const openSettingsDrawer = () => {
    setDrawerOpen(true);

    setSidebarOpen(false);

    setActiveCard(null);

    setActiveMenu("");
  };

  // =====================================
  // CARD CLICK
  // =====================================

  const handleCardClick = (card) => {
    setActiveCard(card.title);

    setActiveMenu(card.items[0] || "");

    setSidebarOpen(true);

    setDrawerOpen(false);
  };

  // =====================================
  // DRAWER ITEM CLICK
  // =====================================

  const handleDrawerItemClick = (cardTitle, item) => {
    console.log("Drawer item clicked:", cardTitle, item);

    setActiveCard(cardTitle);

    setActiveMenu(item);

    setSidebarOpen(true);

    setDrawerOpen(false);

    if (cardTitle.toLowerCase().includes("organis")) {
      if (item.toLowerCase() === "numbering configuration") {
        navigate("/settings/numbering");
      } else if (item.toLowerCase() === "document create") {
        navigate("/documents/create");
      } else if (item.toLowerCase() === "bank") {
        navigate("/settings/bank");
      }
    }
  };

  // =====================================
  // SIDEBAR MENU CLICK
  // =====================================

  const handleSidebarMenuClick = (section, item) => {
    setActiveCard(section);

    setActiveMenu(item);
  };

  // =====================================
  // CLOSE SETTINGS
  // =====================================

  const closeSettings = () => {
    console.log("closeSettings called");

    setSidebarOpen(false);

    setDrawerOpen(false);

    setActiveCard(null);

    setActiveMenu("");
  };

  // =====================================
  // CLOSE DRAWER
  // =====================================

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  // =====================================
  // PROFILE SAVE
  // =====================================

  const handleProfileSave = async (updatedClientData) => {
    try {
      const res = await fetch(`/api/clients/${companyData.client_id}`, {
        method: "PUT",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify(updatedClientData),
      });

      if (!res.ok) {
        throw new Error(`Error saving profile: ${res.statusText}`);
      }

      const result = await res.json();

      if (result.client) {
        setCompany(result.client);

        alert("Profile saved successfully.");
      }
    } catch (error) {
      alert("Failed to save profile: " + error.message);
    }
  };

  // =====================================
  // DIRECT TAX SAVE
  // =====================================

  // =====================================
  // DIRECT TAX SAVE
  // =====================================
  // =====================================
  // DIRECT TAX SAVE
  // =====================================

  const handleDirectTaxSave = async (updatedTaxData) => {
    try {
      const clientId = companyData?.client_id;

      console.log("=================================");
      console.log("📤 DIRECT TAX SAVE");
      console.log("Client ID:", clientId);
      console.log("PAN:", updatedTaxData?.pan);
      console.log("TAN:", updatedTaxData?.tan);
      console.log("=================================");

      if (!clientId) {
        throw new Error("Client ID is missing.");
      }

      const response = await axiosInstance.put(
        `/api/clients/${clientId}/direct-tax`,
        {
          pan: updatedTaxData?.pan || null,
          tan: updatedTaxData?.tan || null,
        },
      );

      console.log("📥 DIRECT TAX RESPONSE:", response.data);

      if (!response.data?.success) {
        throw new Error(
          response.data?.message || "Failed to save Direct Tax details.",
        );
      }

      // =====================================
      // UPDATE COMPANY CONTEXT
      // =====================================

      if (response.data?.client) {
        setCompany((prev) => {
          if (!prev) {
            return response.data.client;
          }

          // Handle both possible company structures
          if (prev.client) {
            return {
              ...prev,
              client: {
                ...prev.client,
                ...response.data.client,
              },
            };
          }

          return {
            ...prev,
            ...response.data.client,
          };
        });
      }

      // =====================================
      // SUCCESS
      // =====================================

      alert("Direct Tax details saved successfully.");
    } catch (error) {
      console.error("❌ DIRECT TAX SAVE ERROR:", error);

      console.error("❌ RESPONSE:", error?.response?.data);

      console.error("❌ STATUS:", error?.response?.status);

      alert(
        "Failed to save Direct Tax details: " +
          (error?.response?.data?.message || error?.message || "Unknown error"),
      );
    }
  };

  // =====================================
  // MSME / UDYAM SAVE
  // =====================================

  const handleMSMESave = async (updatedMSMEData) => {
    try {
      const clientId = companyData?.client_id;

      if (!clientId) {
        throw new Error("Client ID not found.");
      }

      console.log("=================================");
      console.log("📤 MSME / UDYAM SAVE");
      console.log("Client ID:", clientId);
      console.log("MSME Data:", updatedMSMEData);
      console.log("=================================");

      const response = await axiosInstance.put(
        `/api/clients/${clientId}/msme`,
        updatedMSMEData,
      );

      console.log("✅ MSME / UDYAM SAVE RESPONSE:", response.data);

      // Update company data in app context
      if (response.data?.client) {
        setCompany((prev) => ({
          ...prev,
          client: {
            ...(prev?.client || {}),
            ...response.data.client,
          },
        }));
      }

      return response.data;
    } catch (error) {
      console.error("❌ MSME / UDYAM SAVE ERROR:", error);
      console.error("❌ RESPONSE:", error?.response?.data);

      throw error;
    }
  };
  // =====================================
  // RENDER SETTINGS FORM
  // =====================================

  const renderSettingsForm = () => {
    if (!activeCard || !activeMenu) {
      return (
        <div className="p-4 text-gray-500">
          Select a setting to view content.
        </div>
      );
    }

    const key = `${activeCard.toLowerCase()}-${activeMenu
      .toLowerCase()
      .replace(/\s+/g, "-")}`;

    console.log("Form mapping key:", key);

    const formsMap = {
      "organisation-profile": (
        <OrganisationProfileForm
          client={companyData}
          onSave={handleProfileSave}
        />
      ),

      // =====================================================
      // ORGANISATION - BANK
      // =====================================================

      "organisation-bank": <ClientBankForm />,

      "organisation-numbering-configuration": (
        <NumberingConfigForm client_id={companyData?.client_id} />
      ),

      "organisation-document-create": (
        <DocumentCreateForm
          client_id={companyData?.client_id}
          document_type={selectedDocumentType}
        />
      ),

      "integrations & marketplace-whatsapp-cloud-api": (
        <WhatsAppIntegrationForm />
      ),

      "integrations & marketplace-whatsapp-templates": (
        <WhatsAppCloudTemplatesForm />
      ),

      "integrations & marketplace-email-integration": <EmailSettings />,

      "tax & compliance-gst-settings": <GstSettingsForm />,

      "tax & compliance-direct-tax": (
        <DirectTaxSettings initial={companyData} onSave={handleDirectTaxSave} />
      ),

      "tax & compliance-e-invoice": (
        <EInvoiceSettings clientId={companyData?.client_id} />
      ),
      "tax & compliance-msms-settings": (
        <MSMESettings initial={companyData} onSave={handleMSMESave} />
      ),

      // =====================================================
      // OPERATION CONTROLS - TRIPSHEET
      // =====================================================

      "operation controls-tripsheet": <TripsheetSettingsForm />,
      "operation controls-invoice": <InvoiceSettingsForm />,

      // =====================================================
      // USERS & ROLES - USERS
      // =====================================================

      "users & roles-users": <Users clientId={companyData?.client_id} />,

      "users & roles-roles": <Roles clientId={companyData?.client_id} />,
    };

    return (
      formsMap[key] || (
        <div className="p-4 bg-white rounded shadow w-full text-gray-700">
          <h3 className="text-lg font-semibold mb-4">
            {activeCard} - {activeMenu}
          </h3>

          <p>This settings page is under construction.</p>
        </div>
      )
    );
  };

  // =====================================
  // COMPANY LOADING
  // =====================================

  if (!company) {
    return (
      <header
        className="
          fixed
          top-0
          right-0
          h-14
          bg-white
          shadow-sm
          border-b
          border-gray-200
          flex
          items-center
          justify-end
          px-4
          z-40
        "
        style={{
          left: `${sidebarWidth}px`,
        }}
        role="banner"
      >
        <div
          className="
          flex
          items-center
          gap-4
          text-gray-500
          px-4
        "
        >
          Loading company info...
        </div>
      </header>
    );
  }

  // =====================================
  // LOGO
  // =====================================

  const logoSrc = companyData.logo
    ? companyData.logo.startsWith("data:")
      ? companyData.logo
      : `data:image/png;base64,${companyData.logo}`
    : null;

  // =====================================
  // HEADER
  // =====================================

  return (
    <>
      <header
        className="
          fixed
          top-0
          right-0
          h-14
          bg-white
          shadow-sm
          border-b
          border-gray-200
          flex
          items-center
          px-4
          z-40
        "
        style={{
          left: `${sidebarWidth}px`,
        }}
        role="banner"
      >
        {/* =====================================
            TITLE
        ===================================== */}

        <h1
          style={{
            margin: 0,
            fontSize: "22px",
            fontWeight: "bold",
            width: "220px",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            fontFamily: "'Arial', sans-serif",
          }}
        >
          {title || "No Title"}
        </h1>

        {/* =====================================
            TRIAL EXPIRY WARNING
        ===================================== */}

        <div className="flex-1 flex justify-center px-4">
          {isTrialClient && trialExpiryText && (
            <div
              className="
                  bg-red-50
                  border-2
                  border-red-500
                  rounded-lg
                  px-5
                  py-1
                  shadow-sm
                "
            >
              <span
                className="
                    text-red-600
                    font-bold
                    text-base
                    whitespace-nowrap
                  "
              >
                Trial Version Expire on : {trialExpiryText}
              </span>
            </div>
          )}
        </div>

        {/* =====================================
            RIGHT SIDE
        ===================================== */}

        <div
          className="
            flex
            items-center
            gap-4
          "
        >
          {/* Selected Financial Year */}
  {selectedFinancialYear && (
    <div
     className="flex items-center gap-1.5 whitespace-nowrap text-xs text-gray-500"
      title="Selected Financial Year"
    >
      <span className="font-normal">FY:</span>
      <span className="font-semibold">
        {selectedFinancialYear.label}
      </span>
    </div>
  )}
          {/* Company */}

          <div
            className="
              flex
              items-center
              gap-2
              text-sm
              text-gray-700
              cursor-pointer
              select-none
            "
            onClick={toggleMenu}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && toggleMenu()}
            aria-expanded={showMenu}
            aria-haspopup="true"
          >
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={
                  companyData.name ? `${companyData.name} Logo` : "Company Logo"
                }
                className="
                  h-6
                  w-6
                  rounded-full
                  object-cover
                "
                onError={(e) => {
                  e.currentTarget.onerror = null;

                  e.currentTarget.src = "/placeholder-logo.png";
                }}
              />
            ) : (
              <div
                className="
                  h-6
                  w-6
                  rounded-full
                  bg-gray-200
                  flex
                  items-center
                  justify-center
                  text-xs
                  text-gray-500
                "
              >
                N/A
              </div>
            )}

            <span>{companyData.name || "Company Name"}</span>

            <ChevronDown className="w-4 h-4" />
          </div>

          {/* Bell */}
          <Bell
            className="
              w-5
              h-5
              text-gray-700
              hover:text-blue-600
              cursor-pointer
            "
          />

          {/* Settings */}

          <Settings
            className="
              w-5
              h-5
              text-gray-700
              hover:text-blue-600
              cursor-pointer
            "
            onClick={openSettingsDrawer}
            title="Settings"
            aria-label="Open settings"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && openSettingsDrawer()}
          />

          {/* User */}

          <User
            className="
              w-5
              h-5
              text-gray-700
              hover:text-blue-600
              cursor-pointer
            "
          />

          {/* =====================================
              PROFILE MENU
          ===================================== */}

          {showMenu && (
            <div
              className="
                absolute
                top-14
                right-0
                bg-white
                border
                border-gray-200
                rounded
                shadow-md
                z-50
                w-72
              "
              role="menu"
              aria-label="User menu"
            >
              <div
                className="
                  p-3
                  pl-4
                  text-sm
                  text-gray-800
                  text-left
                "
              >
                <p className="font-semibold">
                  {companyData.name || "Company Name"}
                </p>

                <p className="text-xs text-gray-500">
                  {companyData.email || "email@example.com"}
                </p>

                <p
                  className="
                    text-xs
                    text-gray-500
                    whitespace-nowrap
                    overflow-x-auto
                  "
                >
                  Organisation ID: {companyData.client_code || "xxxxxx"}
                </p>

                <hr className="my-2" />

                {/* Financial Year Selector */}
                <div className="flex items-center gap-1.5">
                  <label
                    htmlFor="header-financial-year"
                    className="text-[11px] font-medium text-gray-600 whitespace-nowrap"
                  >
                    FY
                  </label>

                  <select
                    id="header-financial-year"
                    value={selectedFinancialYear?.fyid ?? ""}
                    onChange={(e) => selectFinancialYear(e.target.value)}
                    disabled={
                      financialYearLoading || financialYears.length === 0
                    }
                    className="h-7 w-[94px] rounded border border-gray-000 bg-white px-1.5 text-xs text-gray-700 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                    title="Select Financial Year"
                  >
                    {financialYearLoading && (
                      <option value="">Loading...</option>
                    )}

                    {!financialYearLoading && financialYears.length === 0 && (
                      <option value="">No FY</option>
                    )}

                    {financialYears.map((year) => (
                      <option key={year.fyid} value={year.fyid}>
                        {year.label}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={onViewProfile}
                  className="
                    w-full
                    text-left
                    text-sm
                    hover:bg-gray-100
                    px-0
                    py-1
                  "
                  role="menuitem"
                  tabIndex={0}
                >
                  View Profile
                </button>

                <button
                  onClick={openSettingsDrawer}
                  className="
                    w-full
                    text-left
                    text-sm
                    hover:bg-gray-100
                    px-0
                    py-1
                  "
                  role="menuitem"
                  tabIndex={0}
                >
                  Settings
                </button>

                <button
                  onClick={handleLogout}
                  className="
                    w-full
                    text-left
                    text-sm
                    hover:bg-gray-100
                    px-0
                    py-1
                  "
                  role="menuitem"
                  tabIndex={0}
                >
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* =====================================
          SETTINGS DRAWER
      ===================================== */}

      <SettingsDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        cards={visibleSettingsCards.map((card) => ({
          ...card,
          onClick: () => handleCardClick(card),
          onItemClick: (item) => handleDrawerItemClick(card.title, item),
        }))}
        onMenuClick={handleDrawerItemClick}
      />

      {/* =====================================
          SETTINGS SIDEBAR
      ===================================== */}

      <SettingsSidebar
        open={sidebarOpen}
        onClose={closeSettings}
        sections={visibleSettingsCards}
        activeSection={activeCard}
        activeItem={activeMenu}
        onMenuClick={handleSidebarMenuClick}
      >
        {renderSettingsForm()}
      </SettingsSidebar>
    </>
  );
};

export default Header;
